import type { loginResponseType } from 'apis/handler/auth/authSchema';
import { useModal } from 'context/ModalContext';
import { useTranslation } from 'react-i18next';
import { ApiError } from 'shared/api';
import { useRouteChangeHandler } from 'shared/hooks';
import { getPreferredLanguageCode, sha256 } from 'shared/lib';
import { toast } from 'sonner';
import { useInfoStore } from 'store/useInfoStore';

import { usePostGoogleLogin } from '../api/query';

const GOOGLE_ERROR_KEYS: Record<string, string> = {
  SOCIAL_EMAIL_NOT_VERIFIED: 'google.errors.emailNotVerified',
  DUPLICATED_EMAIL_PLATFORM: 'google.errors.emailInUse',
};

const pushLoginEvent = (data: loginResponseType) => {
  // sha256이 비동기(Web Crypto)라 해시 완료 후 이벤트를 push
  sha256(data.email)
    .catch(() => '')
    .then((hashedEmail) => {
      window.dataLayer?.push({
        event: 'login',
        method: 'google',
        user_signup_date: data.createdAt,
        user_nationality: data.nationality,
        user_occupation: data.occupationType,
        user_visa: data.visaType,
        user_status: 'active',
        user_id: hashedEmail,
      });
    });
};

/** 신규 구글 가입 직후에만 선택형 프로필 혜택 안내. 기존 회원은 미완료여도 원래 목적지로 이동한다. */
export const useGoogleSignIn = () => {
  const { t, i18n } = useTranslation('auth');
  const { setInfo, setProfileCompleted } = useInfoStore();
  const { openModal, MODAL_TYPES } = useModal();
  const { handleLoginSuccess } = useRouteChangeHandler();
  const { mutate, isPending } = usePostGoogleLogin();

  const signInWithGoogle = (idToken: string) => {
    if (isPending) return;

    mutate(
      { idToken, preferredLanguageCode: getPreferredLanguageCode(i18n.language) },
      {
        onSuccess: (data) => {
          setInfo({
            accessToken: data.accessToken,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName ?? '',
            userId: `${data.id}`,
            memberRole: data.memberRole,
          });
          const isProfileCompleted = data.profileCompleted ?? true;
          setProfileCompleted(isProfileCompleted);
          pushLoginEvent(data);

          if (!isProfileCompleted && data.isNewMember === true) {
            // 로그인 모달 → 온보딩 모달로 교체. 가려던 경로(redirectPath)는 유지해 완료 후 이어간다
            openModal(MODAL_TYPES.ONBOARDING);
            return;
          }

          handleLoginSuccess();
        },
        onError: (error) => {
          const errorKey = error instanceof ApiError ? GOOGLE_ERROR_KEYS[error.errorCode ?? ''] : undefined;
          toast.error(t(errorKey ?? 'google.errors.generic'));
        },
      },
    );
  };

  return { signInWithGoogle, isPending };
};
